import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, CheckCircle2, Clock, MessagesSquare, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useContent } from "@/context/ContentContext";
import { Editable } from "@/components/builder/Editable";

const DEFAULT_HERO_IMG = "https://images.unsplash.com/photo-1649989184479-73ab3b750a73?crop=entropy&cs=srgb&fm=jpg&q=80&w=2000";
const TRIAL_URL = "https://app.sedio.it";

const nfIt = new Intl.NumberFormat("it-IT");

/**
 * Anima la sola porzione numerica di una stringa (es. "20h+", "24/7", "1.000+")
 * contando da 0 al valore finale. Conserva prefisso e suffisso non numerici.
 */
function CountUp({ value, delay = 0, duration = 1300 }) {
  const parsed = useMemo(() => {
    const str = String(value ?? "");
    const m = str.match(/[\d.]*\d/);
    if (!m) return { target: null, prefix: str, suffix: "" };
    const raw = m[0];
    return {
      target: parseInt(raw.replace(/\./g, ""), 10),
      prefix: str.slice(0, m.index),
      suffix: str.slice(m.index + raw.length),
    };
  }, [value]);

  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (parsed.target == null) return;
    const reduce = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setCurrent(parsed.target);
      return;
    }
    let interval;
    const timer = setTimeout(() => {
      const startTime = performance.now();
      interval = setInterval(() => {
        const p = Math.min(1, (performance.now() - startTime) / duration);
        const eased = 1 - Math.pow(1 - p, 3);
        setCurrent(Math.round(eased * parsed.target));
        if (p >= 1) clearInterval(interval);
      }, 30);
    }, delay);
    return () => {
      clearTimeout(timer);
      if (interval) clearInterval(interval);
    };
  }, [parsed.target, duration, delay]);

  if (parsed.target == null) return <span>{value}</span>;
  return (
    <span>
      {parsed.prefix}
      {nfIt.format(current)}
      {parsed.suffix}
    </span>
  );
}

const STATS = [
  { icon: Clock, key: "s1", value: "20h+", label: "risparmiate per condominio ogni anno" },
  { icon: MessagesSquare, key: "s2", value: "3", label: "canali (Email, PEC, WhatsApp) in un unico posto" },
  { icon: Zap, key: "s3", value: "24/7", label: "assistente AI sempre operativo" },
];

// Varianti animazione ingresso (solo transform + opacity per performance)
const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.05 } },
};
const item = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};
const titleItem = {
  hidden: { opacity: 0, y: 34, scale: 0.96 },
  show: { opacity: 1, y: 0, scale: 1, transition: { type: "spring", stiffness: 120, damping: 16, mass: 0.9 } },
};

export const Hero = () => {
  const { getText, ctaClick, authed } = useContent();
  const heroImg = getText("hero.image", "") || DEFAULT_HERO_IMG;
  return (
    <section className="relative overflow-hidden">
      <img src={heroImg} alt="Palazzi residenziali moderni" className="absolute inset-0 h-full w-full object-cover" />
      {/* Overlay alleggerito: scuro dove c'e il testo (sinistra), piu trasparente a destra per far vedere la foto */}
      <div className="absolute inset-0" style={{ backgroundColor: "hsl(226 50% 8% / 0.42)" }} aria-hidden="true" />
      <div
        className="absolute inset-0"
        style={{ background: "linear-gradient(to right, hsl(226 55% 6% / 0.90) 0%, hsl(226 50% 9% / 0.62) 46%, hsl(221 45% 14% / 0.28) 100%)" }}
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:pb-28 lg:pt-24">
        <motion.div variants={container} initial="hidden" animate="show" className="grid items-center gap-10 lg:grid-cols-12 lg:gap-14">
          {/* Colonna testo (sinistra) */}
          <div className="lg:col-span-7">
            <motion.p variants={item} className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-cyan-200 backdrop-blur">
              <Sparkles className="h-3.5 w-3.5" /> <Editable path="hero.eyebrow">La rivoluzione nella gestione del condominio</Editable>
            </motion.p>
            <motion.div variants={titleItem}>
              <Editable as="h1" path="hero.title" className="block font-display text-5xl font-extrabold leading-[0.98] text-white [text-shadow:0_2px_28px_hsl(226_60%_4%/0.75)] sm:text-6xl lg:text-7xl">
                Rivoluziona la gestione del condominio.
              </Editable>
            </motion.div>
            <motion.div variants={item}>
              <Editable as="p" path="hero.subtitle" className="mt-6 block max-w-2xl text-base leading-7 text-slate-100 [text-shadow:0_1px_16px_hsl(226_60%_4%/0.7)] md:text-lg">
                Sedio cambia per sempre il modo di amministrare e di comunicare con i condomini. L'intelligenza artificiale raccoglie i messaggi da email, PEC e WhatsApp, li comprende e li trasforma in azioni concrete: meno lavoro ripetitivo, comunicazioni sempre puntuali e il pieno controllo nelle tue mani.
              </Editable>
            </motion.div>
            <motion.div variants={item} className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button data-testid="hero-primary-cta-button" size="lg" onClick={ctaClick("hero.ctaPrimary.link", TRIAL_URL)} className="btn-cta h-12 gap-2 px-7 text-base">
                <Editable path="hero.ctaPrimary">Provalo gratis</Editable> <ArrowRight className="h-4 w-4" />
              </Button>
              <Button data-testid="hero-secondary-cta-button" size="lg" onClick={ctaClick("hero.ctaSecondary.link", "funzioni")} className="h-12 rounded-full border border-white/25 bg-white/10 px-7 text-base text-white hover:bg-white/15 hover:text-white">
                <Editable path="hero.ctaSecondary">Scopri le funzioni</Editable>
              </Button>
            </motion.div>
            <motion.div variants={item} className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-100 [text-shadow:0_1px_10px_hsl(226_60%_4%/0.6)]">
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-cyan-300" /> <Editable path="hero.trust1">Email, PEC e WhatsApp integrati</Editable></span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-cyan-300" /> <Editable path="hero.trust3">Provalo gratis, senza impegno</Editable></span>
            </motion.div>
          </div>

          {/* Statistiche d'impatto (destra, impilate) */}
          <motion.div variants={item} className="flex flex-col gap-4 lg:col-span-5" data-testid="hero-stats">
            {STATS.map((s, i) => (
              <motion.div
                key={s.key}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.7 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`hero-stat-${s.key}`}
                className="flex items-center gap-4 rounded-2xl border border-white/12 p-5 backdrop-blur-md"
                style={{ backgroundColor: "hsl(226 52% 9% / 0.80)" }}
              >
                <span className="inline-flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-cyan-400/20 text-cyan-200"><s.icon className="h-6 w-6" strokeWidth={2} /></span>
                <div>
                  <p className="font-display text-3xl font-extrabold leading-none text-white sm:text-4xl">
                    {authed
                      ? <Editable path={`hero.${s.key}.value`}>{s.value}</Editable>
                      : <CountUp value={getText(`hero.${s.key}.value`, s.value)} delay={700 + i * 120} />}
                  </p>
                  <p className="mt-1.5 text-sm leading-5 text-slate-200"><Editable path={`hero.${s.key}.label`}>{s.label}</Editable></p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
